import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LinkIcon from "@mui/icons-material/Link";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import { toast } from "react-toastify";
import axiosInstance from "../../axios/axios";
import { END_POINTS } from "../../shared";
import AgGridTable from "../../components/table/AgGridTable";
import { getVendorLogo } from "./vendorLogoUtils";
import MapTemplateConfirmDialog, {
  MappingStatus,
  MapConfirmDeviceRow,
} from "./MapTemplateConfirmDialog";

type StatusFilter = "all" | "not_mapped" | "mapped";

type DeviceRow = MapConfirmDeviceRow & {
  ip_address: string;
  vendor: string;
  category: string;
  model: string;
  type: string;
  criticality: string;
  mapping_status_label: string;
};

type Props = {
  templateId: string;
  templateName?: string;
  vendorName?: string;
  deviceModelName?: string;
  isDarkMode: boolean;
  /** When false, Map is disabled (template must be Active + Success). */
  mappingEnabled?: boolean;
  initialStatusFilter?: StatusFilter;
  templateNamesById?: Record<string, string>;
  onMappingCountChange?: (count: number) => void;
  onDevicesStatsChange?: (stats: { mapped: number; total: number }) => void;
};

const MAPPING_NOT_READY_MSG =
  "Asset mapping is available only for Active templates with a Success status.";

const norm = (v: any) => String(v ?? "").trim().toLowerCase();

const isCloudAsset = (row: any): boolean => {
  if (!row) return false;
  const vendorStr = norm(row.vendor);
  const cloudStr = norm(row.cloud);
  const typeStr = norm(row.type);
  return (
    typeStr === "cloud" ||
    cloudStr.length > 0 ||
    vendorStr === "amazon web services" ||
    vendorStr === "aws" ||
    vendorStr.includes("amazon") ||
    vendorStr.includes("aws") ||
    vendorStr.includes("azure") ||
    vendorStr.includes("gcp") ||
    vendorStr.includes("google cloud")
  );
};

const isOnpremAsset = (row: any): boolean => {
  const typeStr = norm(row?.type);
  if (typeStr === "onprem" || typeStr === "on_prem" || typeStr === "on-premise") {
    return true;
  }
  return !isCloudAsset(row);
};

const matchesVendorAndModel = (
  row: any,
  vendorName?: string,
  deviceModelName?: string
): boolean => {
  const vendorFilter = norm(vendorName);
  const modelFilter = norm(deviceModelName);
  if (vendorFilter) {
    const rowVendor = norm(row.vendor || row.vendor_name);
    if (rowVendor !== vendorFilter) return false;
  }
  if (modelFilter) {
    // Exact OS/model match only. Do NOT use includes():
    // "ios xe".includes("ios") would wrongly keep IOS XE on an IOS template.
    const model = norm(row.model || row.device_model_name || row.os);
    if (model !== modelFilter) return false;
  }
  return true;
};

const isMappedToTemplate = (row: DeviceRow) => row.mapping_status === "mapped";
const isNotMappedToTemplate = (row: DeviceRow) => row.mapping_status !== "mapped";

const CRITICALITY_COLORS: Record<string, string> = {
  critical: "#B71E31",
  high: "#FF6600",
  medium: "#E0B102",
  low: "#00A61B",
  default: "#B71E31",
};

const getCriticalityColor = (criticality?: string) =>
  CRITICALITY_COLORS[String(criticality || "").toLowerCase()] ||
  CRITICALITY_COLORS.default;

const MasterConfigDevicesTab: React.FC<Props> = ({
  templateId,
  templateName,
  vendorName,
  deviceModelName,
  isDarkMode,
  mappingEnabled = true,
  initialStatusFilter = "all",
  templateNamesById = {},
  onMappingCountChange,
  onDevicesStatsChange,
}) => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<DeviceRow[]>([]);
  const [selected, setSelected] = useState<DeviceRow[]>([]);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(initialStatusFilter);
  const [confirmAction, setConfirmAction] = useState<"map" | "unmap" | null>(null);
  const [pendingRows, setPendingRows] = useState<DeviceRow[]>([]);
  const [saving, setSaving] = useState(false);
  const gridRef = useRef<any>(null);

  useEffect(() => {
    setStatusFilter(initialStatusFilter || "all");
  }, [templateId, initialStatusFilter]);

  const onMappingCountChangeRef = useRef(onMappingCountChange);
  const onDevicesStatsChangeRef = useRef(onDevicesStatsChange);
  useEffect(() => {
    onMappingCountChangeRef.current = onMappingCountChange;
    onDevicesStatsChangeRef.current = onDevicesStatsChange;
  }, [onMappingCountChange, onDevicesStatsChange]);

  const loadDevices = useCallback(async () => {
    if (!templateId) return;
    setLoading(true);
    try {
      const [assetsRes, mappingsRes] = await Promise.all([
        axiosInstance.post(END_POINTS.DEVICE_LIST_V2, { type: 1 }),
        axiosInstance.get(END_POINTS.MASTER_TEMPLATE_DEVICE_MAPPING, {
          params: { active: true },
        }),
      ]);

      const assetList =
        assetsRes?.data?.data?.device_list ||
        assetsRes?.data?.data ||
        assetsRes?.data ||
        [];
      const assets = Array.isArray(assetList) ? assetList : [];

      const mappingList = Array.isArray(mappingsRes?.data?.data)
        ? mappingsRes.data.data
        : Array.isArray(mappingsRes?.data)
          ? mappingsRes.data
          : [];

      const mappingByDevice = new Map<string, string>();
      mappingList.forEach((m: any) => {
        if (m?.active === false) return;
        const deviceId = String(m.scan_device_id || "");
        const tplId = String(m.template_id || "");
        if (deviceId && tplId) mappingByDevice.set(deviceId, tplId);
      });

      const resolveTemplateName = (tplId: string | null) => {
        if (!tplId) return null;
        return (
          templateNamesById[tplId] ||
          (tplId === String(templateId) ? templateName : null) ||
          null
        );
      };

      const built: DeviceRow[] = assets
        .filter((d: any) => isOnpremAsset(d))
        .filter((d: any) => matchesVendorAndModel(d, vendorName, deviceModelName))
        .map((d: any) => {
          const id = String(d.id || d.device_id || d.scan_device_id || "");
          const mappedTpl = mappingByDevice.get(id) || null;
          let mapping_status: MappingStatus = "unmapped";
          let mapping_status_label = "Not mapped";
          if (mappedTpl && mappedTpl === String(templateId)) {
            mapping_status = "mapped";
            mapping_status_label = "Mapped";
          } else if (mappedTpl) {
            mapping_status = "mapped_elsewhere";
            mapping_status_label = "Not mapped";
          }
          return {
            scan_device_id: id,
            hostname: d.host_name || d.hostname || d.device_name || d.name || "-",
            ip_address: d.ip_address || d.host_ip_address || d.host || d.ip || "-",
            vendor: d.vendor || d.vendor_name || "-",
            category: d.device_type || d.category || "-",
            model: d.model || d.device_model_name || "-",
            type: d.type || "onprem",
            criticality: d.criticality || "",
            mapping_status,
            mapping_status_label,
            mapped_template_id: mappedTpl,
            mapped_template_name: resolveTemplateName(mappedTpl),
          };
        })
        .filter((d: DeviceRow) => Boolean(d.scan_device_id));

      setRows(built);
      setSelected([]);
      const mappedCount = built.filter((r) => r.mapping_status === "mapped").length;
      onMappingCountChangeRef.current?.(mappedCount);
      onDevicesStatsChangeRef.current?.({ mapped: mappedCount, total: built.length });
    } catch (error) {
      console.error("Failed to load devices for template mapping", error);
      toast.error("Failed to load devices from Asset Inventory");
      setRows([]);
      onMappingCountChangeRef.current?.(0);
      onDevicesStatsChangeRef.current?.({ mapped: 0, total: 0 });
    } finally {
      setLoading(false);
    }
  }, [templateId, templateName, vendorName, deviceModelName, templateNamesById]);

  useEffect(() => {
    loadDevices();
  }, [loadDevices]);

  const counts = useMemo(() => {
    const total = rows.length;
    const mapped = rows.filter(isMappedToTemplate).length;
    const notMapped = rows.filter(isNotMappedToTemplate).length;
    return { total, mapped, notMapped };
  }, [rows]);

  const filteredRows = useMemo(() => {
    if (statusFilter === "mapped") return rows.filter(isMappedToTemplate);
    if (statusFilter === "not_mapped") return rows.filter(isNotMappedToTemplate);
    return rows;
  }, [rows, statusFilter]);

  const runMap = useCallback(
    async (targets: DeviceRow[]) => {
      if (!targets.length) {
        toast.error("Select devices to map");
        return;
      }
      if (!mappingEnabled) {
        toast.info(MAPPING_NOT_READY_MSG);
        return;
      }
      setSaving(true);
      try {
        const response = await axiosInstance.post(
          END_POINTS.MASTER_TEMPLATE_DEVICE_MAPPING,
          {
            template_id: templateId,
            scan_device_ids: targets.map((r) => r.scan_device_id),
          }
        );
        if (response?.status === 200 || response?.data?.status_code === 200) {
          toast.success(
            `Mapped ${targets.length} device${targets.length > 1 ? "s" : ""}`
          );
          setConfirmAction(null);
          setPendingRows([]);
          await loadDevices();
        } else {
          toast.error(response?.data?.message || "Failed to map devices");
        }
      } catch (error: any) {
        console.error("Map failed", error);
        toast.error(error?.response?.data?.message || "Failed to map devices");
      } finally {
        setSaving(false);
      }
    },
    [templateId, loadDevices, mappingEnabled]
  );

  const runUnmap = useCallback(
    async (targets: DeviceRow[]) => {
      if (!targets.length) {
        toast.error("Select mapped devices to unmap");
        return;
      }
      setSaving(true);
      try {
        const response = await axiosInstance.post(
          END_POINTS.MASTER_TEMPLATE_DEVICE_MAPPING,
          {
            action: "unmap",
            scan_device_ids: targets.map((r) => r.scan_device_id),
          }
        );
        if (response?.status === 200 || response?.data?.status_code === 200) {
          toast.success(
            `Unmapped ${targets.length} device${targets.length > 1 ? "s" : ""}`
          );
          setConfirmAction(null);
          setPendingRows([]);
          await loadDevices();
        } else {
          toast.error(response?.data?.message || "Failed to unmap");
        }
      } catch (error: any) {
        console.error("Unmap failed", error);
        toast.error(error?.response?.data?.message || "Failed to unmap devices");
      } finally {
        setSaving(false);
      }
    },
    [loadDevices]
  );

  const requestMap = useCallback(
    (targets: DeviceRow[]) => {
      if (!targets.length) return;
      if (!mappingEnabled) {
        toast.info(MAPPING_NOT_READY_MSG);
        return;
      }
      setPendingRows(targets);
      setConfirmAction("map");
    },
    [mappingEnabled]
  );

  const requestUnmap = useCallback(
    (targets: DeviceRow[]) => {
      if (!targets.length) return;
      setPendingRows(targets);
      setConfirmAction("unmap");
    },
    []
  );

  const actionHandlersRef = useRef({
    requestMap,
    requestUnmap,
    saving,
    mappingEnabled,
  });
  useEffect(() => {
    actionHandlersRef.current = {
      requestMap,
      requestUnmap,
      saving,
      mappingEnabled,
    };
  }, [requestMap, requestUnmap, saving, mappingEnabled]);

  const selectedForMap = useMemo(
    () => selected.filter(isNotMappedToTemplate),
    [selected]
  );
  const selectedForUnmap = useMemo(
    () => selected.filter(isMappedToTemplate),
    [selected]
  );

  const handleSelectionChange = useCallback((rows: any[]) => {
    const next = Array.isArray(rows) ? rows : [];
    setSelected((prev) => {
      if (
        prev.length === next.length &&
        prev.every((row, i) => row?.scan_device_id === next[i]?.scan_device_id)
      ) {
        return prev;
      }
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => {
    gridRef.current?.api?.deselectAll?.();
    setSelected([]);
  }, []);

  const columnDefs = useMemo<ColDef[]>(
    () => [
      {
        field: "selection",
        headerName: "",
        minWidth: 48,
        width: 48,
        maxWidth: 52,
        flex: 0,
        checkboxSelection: true,
        headerCheckboxSelection: true,
        sortable: false,
        filter: false,
        resizable: false,
        suppressMovable: true,
        suppressMenu: true,
        suppressHeaderMenuButton: true,
        suppressColumnsToolPanel: true,
      },
      {
        field: "number",
        headerName: "#",
        minWidth: 56,
        width: 56,
        flex: 0,
        filter: false,
        sortable: false,
        resizable: true,
        suppressMovable: true,
        suppressMenu: true,
        suppressHeaderMenuButton: true,
        suppressColumnsToolPanel: true,
        menuTabs: [],
        valueGetter: (params: any) =>
          params.node?.rowIndex === null || params.node?.rowIndex === undefined
            ? "-"
            : params.node.rowIndex + 1,
        cellStyle: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          paddingLeft: 0,
          paddingRight: 0,
        },
        headerStyle: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        },
        headerComponent: () => (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 700,
              fontSize: "13px",
              lineHeight: 1,
              overflow: "visible",
              color: isDarkMode ? "#E5E7EB" : "#344054",
            }}
          >
            #
          </Box>
        ),
      },
      {
        field: "hostname",
        headerName: "Name",
        // Fixed widths (no flex) so columns don't shrink in the drawer and
        // horizontal scroll can reveal OS / Type / Status.
        flex: 0,
        width: 200,
        minWidth: 180,
        cellStyle: {
          display: "flex",
          alignItems: "center",
          paddingLeft: 8,
        },
        cellRenderer: (params: ICellRendererParams) => {
          const name = params.data?.hostname || params.value || "-";
          const criticality = params.data?.criticality || "";
          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                overflow: "hidden",
                height: "100%",
              }}
            >
              <Box
                title={criticality ? `Criticality: ${criticality}` : "Criticality"}
                sx={{
                  width: "4px",
                  height: "28px",
                  bgcolor: getCriticalityColor(criticality),
                  borderRadius: "2px",
                  mr: 1,
                  flexShrink: 0,
                }}
              />
              <Typography
                title={name}
                sx={{
                  fontSize: "13px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  color: isDarkMode ? "#E5E7EB" : "#101828",
                }}
              >
                {name}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: "ip_address",
        headerName: "IP",
        flex: 0,
        width: 140,
        minWidth: 130,
      },
      {
        field: "vendor",
        headerName: "Vendor",
        flex: 0,
        width: 160,
        minWidth: 140,
        cellStyle: {
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
        },
        cellRenderer: (params: ICellRendererParams) => {
          const name = params.data?.vendor || params.value || "";
          const logo = getVendorLogo(name, isDarkMode);
          if (logo) {
            return (
              <Box
                sx={{ display: "flex", alignItems: "center", height: "100%" }}
                title={name}
              >
                <img
                  src={logo.src}
                  alt={logo.alt}
                  style={{
                    maxHeight: 32,
                    maxWidth: 84,
                    objectFit: "contain",
                    objectPosition: "left center",
                    ...(logo.style || {}),
                  }}
                />
              </Box>
            );
          }
          return (
            <Typography
              title={name}
              sx={{
                display: "flex",
                alignItems: "center",
                height: "100%",
                fontSize: "13px",
                textOverflow: "ellipsis",
                overflow: "hidden",
                whiteSpace: "nowrap",
              }}
            >
              {name || "-"}
            </Typography>
          );
        },
      },
      {
        field: "model",
        headerName: "OS",
        flex: 0,
        width: 120,
        minWidth: 110,
      },
      {
        field: "category",
        headerName: "Type",
        flex: 0,
        width: 120,
        minWidth: 110,
      },
      {
        field: "mapping_status",
        headerName: "Status",
        flex: 0,
        width: 150,
        minWidth: 140,
        sortable: false,
        cellStyle: {
          display: "flex",
          alignItems: "center",
        },
        cellRenderer: (params: ICellRendererParams) => {
          const row = params.data as DeviceRow | undefined;
          if (!row) return null;
          const mapped = isMappedToTemplate(row);
          const {
            requestMap: onMap,
            requestUnmap: onUnmap,
            saving: isSaving,
            mappingEnabled: canMap,
          } = actionHandlersRef.current;
          const mapDisabled = isSaving || (!mapped && !canMap);

          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                height: "100%",
                gap: 1,
                pr: 0.5,
              }}
            >
              <Tooltip title={mapped ? "Mapped" : "Not mapped"} arrow>
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 28,
                    height: 28,
                  }}
                >
                  {mapped ? (
                    <CheckCircleOutlineIcon
                      sx={{ fontSize: 22, color: "#15803D" }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: 18,
                        height: 18,
                        borderRadius: "50%",
                        border: isDarkMode
                          ? "1.5px dashed #6B7280"
                          : "1.5px dashed #98A2B3",
                      }}
                    />
                  )}
                </Box>
              </Tooltip>

              <Tooltip
                title={
                  mapped
                    ? "Unmap"
                    : canMap
                      ? "Map"
                      : MAPPING_NOT_READY_MSG
                }
                arrow
              >
                <span>
                  <IconButton
                    size="small"
                    disabled={mapDisabled}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (mapped) onUnmap([row]);
                      else onMap([row]);
                    }}
                    sx={{
                      p: 0.5,
                      color: mapped
                        ? isDarkMode
                          ? "#9CA3AF"
                          : "#98A2B3"
                        : "#3A2ADA",
                      "&:hover": {
                        bgcolor: mapped
                          ? isDarkMode
                            ? "rgba(255,255,255,0.06)"
                            : "rgba(16,24,40,0.04)"
                          : "rgba(58, 42, 218, 0.08)",
                      },
                    }}
                    aria-label={mapped ? "Unmap" : "Map"}
                  >
                    {mapped ? (
                      <LinkOffIcon sx={{ fontSize: 18 }} />
                    ) : (
                      <LinkIcon sx={{ fontSize: 18 }} />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          );
        },
      },
    ],
    [isDarkMode]
  );

  const filterPillSx = (active: boolean) => {
    const activeColor = "#3A2ADA";
    const idleColor = isDarkMode ? "#D1D5DB" : "#667085";
    const labelColor = active ? activeColor : idleColor;
    return {
      textTransform: "none" as const,
      fontWeight: 700,
      fontSize: "12px",
      borderRadius: "8px",
      px: 1.5,
      py: 0.4,
      minWidth: 0,
      border: active
        ? "1px solid rgba(58, 42, 218, 0.35)"
        : isDarkMode
          ? "1px solid #374151"
          : "1px solid #E4E7EC",
      bgcolor: active
        ? isDarkMode
          ? "rgba(58, 42, 218, 0.25)"
          : "#EEF2FF"
        : "transparent",
      color: labelColor,
      // Keep label readable on hover (MUI can flip text to white)
      "&:hover": {
        bgcolor: active
          ? isDarkMode
            ? "rgba(58, 42, 218, 0.35)"
            : "#E0E7FF"
          : isDarkMode
            ? "rgba(255,255,255,0.06)"
            : "#F2F4F7",
        borderColor: active
          ? "rgba(58, 42, 218, 0.45)"
          : isDarkMode
            ? "#4B5563"
            : "#D0D5DD",
        color: labelColor,
      },
      "&.MuiButton-root:hover": {
        color: labelColor,
      },
    };
  };

  return (
    <Box sx={{ flex: 1, minHeight: 0, height: "100%", display: "flex", flexDirection: "column" }}>
      {loading ? (
        <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <CircularProgress size={36} sx={{ color: "#3A2ADA" }} />
        </Box>
      ) : (
        <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
          {!mappingEnabled && (
            <Box
              sx={{
                mb: 1.25,
                px: 1.5,
                py: 1,
                borderRadius: "8px",
                bgcolor: isDarkMode ? "rgba(58, 42, 218, 0.12)" : "#EEF4FF",
                border: `1px solid ${
                  isDarkMode ? "rgba(58, 42, 218, 0.35)" : "rgba(58, 42, 218, 0.25)"
                }`,
              }}
            >
              <Typography
                sx={{
                  fontSize: "12.5px",
                  fontWeight: 600,
                  color: isDarkMode ? "#C7D2FE" : "#3A2ADA",
                  lineHeight: 1.4,
                }}
              >
                {MAPPING_NOT_READY_MSG}
              </Typography>
            </Box>
          )}
          <AgGridTable
            columnDefs={columnDefs}
            data={filteredRows}
            showSearch
            searchPlaceholder="Search hostname, IP or vendor"
            pagination
            paginationPageSize={20}
            height="100%"
            isRowSelectable
            checkboxSelection={false}
            headerCheckboxSelection={false}
            rowSelection="multiple"
            gridRef={gridRef}
            onSelectionChange={handleSelectionChange}
            gridOptions={{
              // Keep columns at declared widths so the body can scroll horizontally
              // inside the template drawer (flex:1 default would crush OS/Type/Status).
              suppressHorizontalScroll: false,
              alwaysShowHorizontalScroll: false,
            }}
            styles={{
              // Easier to grab the horizontal scrollbar in the drawer
              "& .ag-body-horizontal-scroll-viewport::-webkit-scrollbar": {
                height: "8px !important",
              },
            }}
            headerActions={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                {selected.length > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                      mr: 0.5,
                      pr: 1.25,
                      borderRight: isDarkMode ? "1px solid #374151" : "1px solid #E4E7EC",
                    }}
                  >
                    {selectedForMap.length > 0 && (
                      <Button
                        size="small"
                        variant="contained"
                        disabled={saving || !mappingEnabled}
                        onClick={() => requestMap(selectedForMap)}
                        startIcon={<LinkIcon sx={{ fontSize: "16px !important" }} />}
                        title={
                          mappingEnabled ? undefined : MAPPING_NOT_READY_MSG
                        }
                        sx={{
                          textTransform: "none",
                          fontWeight: 700,
                          fontSize: "12px",
                          borderRadius: "8px",
                          px: 1.5,
                          py: 0.4,
                          boxShadow: "none",
                          bgcolor: "#3A2ADA",
                          color: "#FFFFFF",
                          "&:hover": {
                            bgcolor: "#2E21B6",
                            boxShadow: "none",
                            color: "#FFFFFF",
                          },
                          "&.Mui-disabled": {
                            bgcolor: isDarkMode
                              ? "rgba(58,42,218,0.35)"
                              : "rgba(58,42,218,0.4)",
                            color: isDarkMode
                              ? "rgba(255,255,255,0.5)"
                              : "rgba(255,255,255,0.8)",
                          },
                        }}
                      >
                        Map ({selectedForMap.length})
                      </Button>
                    )}
                    {selectedForUnmap.length > 0 && (
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={saving}
                        onClick={() => requestUnmap(selectedForUnmap)}
                        startIcon={<LinkOffIcon sx={{ fontSize: "16px !important" }} />}
                        sx={{
                          textTransform: "none",
                          fontWeight: 700,
                          fontSize: "12px",
                          borderRadius: "8px",
                          px: 1.5,
                          py: 0.4,
                          borderColor: isDarkMode ? "rgba(248,113,113,0.45)" : "#FDA29B",
                          color: isDarkMode ? "#FCA5A5" : "#B42318",
                          bgcolor: isDarkMode ? "rgba(248,113,113,0.08)" : "#FEF3F2",
                          "&.MuiButton-outlined:hover": {
                            borderColor: isDarkMode ? "#FCA5A5" : "#F97066",
                            backgroundColor: isDarkMode
                              ? "rgba(248,113,113,0.14)"
                              : "#FEE4E2",
                            color: isDarkMode ? "#FCA5A5" : "#B42318",
                          },
                        }}
                      >
                        Unmap ({selectedForUnmap.length})
                      </Button>
                    )}
                    <Button
                      size="small"
                      variant="text"
                      onClick={clearSelection}
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: "12px",
                        minWidth: 0,
                        px: 1,
                        color: isDarkMode ? "#9CA3AF" : "#667085",
                        "&.MuiButton-text:hover": {
                          backgroundColor: isDarkMode
                            ? "rgba(255,255,255,0.06)"
                            : "#F2F4F7",
                          color: isDarkMode ? "#D1D5DB" : "#344054",
                        },
                      }}
                    >
                      Clear
                    </Button>
                  </Box>
                )}
                <Button
                  size="small"
                  onClick={() => setStatusFilter("all")}
                  sx={filterPillSx(statusFilter === "all")}
                >
                  All {counts.total}
                </Button>
                <Button
                  size="small"
                  onClick={() => setStatusFilter("not_mapped")}
                  sx={filterPillSx(statusFilter === "not_mapped")}
                >
                  Not mapped {counts.notMapped}
                </Button>
                <Button
                  size="small"
                  onClick={() => setStatusFilter("mapped")}
                  sx={filterPillSx(statusFilter === "mapped")}
                >
                  Mapped {counts.mapped}
                </Button>
              </Box>
            }
          />
        </Box>
      )}

      <MapTemplateConfirmDialog
        open={Boolean(confirmAction)}
        action={confirmAction}
        rows={pendingRows}
        templateName={templateName}
        templateNamesById={templateNamesById}
        isDarkMode={isDarkMode}
        saving={saving}
        onClose={() => {
          if (saving) return;
          setConfirmAction(null);
          setPendingRows([]);
        }}
        onConfirm={() =>
          confirmAction === "unmap"
            ? runUnmap(pendingRows)
            : runMap(pendingRows)
        }
      />
    </Box>
  );
};

export default MasterConfigDevicesTab;
